import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Trash2, FileText } from "lucide-react";
import { UNDERLAG_I18N } from "./underlagI18n";
import { computeHours, fmtH, nonWorkingReason, NON_WORKING_LABELS } from "./underlagHours";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UnderlagSign() {
  const { signToken, slug } = useParams();
  const [data, setData] = useState(null);
  const [resolvedSignToken, setResolvedSignToken] = useState(signToken || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [alreadySigned, setAlreadySigned] = useState(false);

  const [customerEmail, setCustomerEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [notPresent, setNotPresent] = useState(false);
  const signaturePad = useRef(null);

  const [lang, setLang] = useState("no");
  const [lightbox, setLightbox] = useState(null); // { url, name } or null
  const t = UNDERLAG_I18N[lang];

  useEffect(() => {
    (async () => {
      try {
        const url = signToken
          ? `${API}/underlag/sign/${signToken}`
          : `${API}/underlag/u/${slug}`;
        const { data } = await axios.get(url);
        setData(data);
        setResolvedSignToken(data.sign_token || signToken);
        setLang(data.language || "no");
        setCustomerEmail(data.customer_email || "");
        setFirstName(data.customer_first_name || "");
        setLastName(data.customer_last_name || "");
      } catch (e) {
        if (e.response?.status === 410) setAlreadySigned(true);
        else setError(e.response?.data?.detail || "Link error");
      } finally {
        setLoading(false);
      }
    })();
  }, [signToken, slug]);

  const clearSig = () => signaturePad.current?.clear();

  const submit = async () => {
    // Email is optional when customer is not present (we don't know it and don't need it)
    if (!notPresent) {
      if (!customerEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail.trim())) {
        toast.error(t.yourEmail + " — " + t.required);
        return;
      }
      if (!firstName.trim() || !lastName.trim()) {
        toast.error(t.firstName + " / " + t.lastName + " — " + t.required);
        return;
      }
    }
    let sigDataUrl = "";
    if (!notPresent) {
      if (signaturePad.current?.isEmpty()) {
        toast.error(t.signature);
        return;
      }
      // Note: getTrimmedCanvas() is broken in react-signature-canvas@1.1.0-alpha due to
      // trim-canvas CJS/ESM interop. Use raw canvas toDataURL directly (trim is cosmetic).
      sigDataUrl = signaturePad.current.toDataURL("image/png");
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/underlag/sign/${resolvedSignToken}`, {
        customer_email: customerEmail.trim(),
        customer_first_name: firstName.trim(),
        customer_last_name: lastName.trim(),
        signature_data_url: sigDataUrl,
        customer_not_present: notPresent,
      });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  if (alreadySigned) {
    return <FullScreenMessage title="✅" text={UNDERLAG_I18N.no.alreadySigned} />;
  }
  if (error) {
    return <FullScreenMessage title="⚠️" text={error} />;
  }
  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-cyan-50 to-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center relative overflow-hidden">
          {/* Decorative gradient corner */}
          <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gradient-to-br from-emerald-200 to-cyan-200 opacity-40 blur-2xl" />

          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-5 shadow-lg shadow-emerald-500/30">
              <CheckCircle2 className="w-12 h-12 text-white" strokeWidth={2.5} />
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-2 tracking-tight">
              {t.signSuccessTitle}
            </h1>
            <p className="text-slate-600 mb-6" data-testid="sign-success-msg">
              {t.signSuccessText}
            </p>

            {data?.number && (
              <div className="bg-slate-900 rounded-xl p-4 mb-2 shadow-md" data-testid="sign-success-number">
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">
                  {t.number}
                </div>
                <div className="text-4xl sm:text-5xl font-black text-cyan-400 tracking-tight">
                  #{data.number}
                </div>
              </div>
            )}

            {(firstName || lastName) && (
              <p className="text-xs text-slate-500 mt-3">
                {t.signedBy || "Signert av"}{" "}
                <span className="font-semibold text-slate-700">
                  {`${firstName} ${lastName}`.trim()}
                </span>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const workerRows = (data.workers || []).filter((w) => w.name || w.job || w.description);
  const imgAttachments = (data.attachments || []).filter((a) => a.type === "image");
  const otherAttachments = (data.attachments || []).filter((a) => a.type !== "image");

  return (
    <div className="light-theme min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pb-16 text-slate-900">
      {/* Header */}
      <div className="bg-slate-900 text-white shadow-lg">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <div className="text-lg sm:text-2xl font-bold tracking-tight">
              Doserne <span className="text-cyan-400">Stillas</span>
              <span className="text-slate-400 text-sm sm:text-base ml-1">AS</span>
            </div>
            <div className="text-[10px] sm:text-xs text-slate-400 mt-0.5">{t.subtitle}</div>
          </div>
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs sm:text-sm"
            data-testid="sign-lang-selector"
          >
            <option value="no">🇳🇴 NO</option>
            <option value="en">🇬🇧 EN</option>
            <option value="ro">🇷🇴 RO</option>
            <option value="pl">🇵🇱 PL</option>
          </select>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-3 sm:px-6 py-6 space-y-5">
        {/* Number */}
        <div className="flex items-baseline justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t.signTitle}</h1>
            <p className="text-sm text-slate-600 mt-1">{t.signSubtitle}</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">{t.number}</div>
            <div className="text-2xl sm:text-3xl font-bold text-cyan-600" data-testid="sign-number">#{data.number}</div>
          </div>
        </div>

        {/* Read-only summary */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 space-y-4">
          {(() => {
            const nwr = nonWorkingReason(data.date);
            if (!nwr) return null;
            const label = (NON_WORKING_LABELS[lang] || NON_WORKING_LABELS.no)[nwr];
            return (
              <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-3 text-amber-900 text-sm font-semibold" data-testid="sign-non-working-banner">
                {label}
              </div>
            );
          })()}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <KV label={t.date} value={data.date} />
            <KV label={t.address} value={data.address} />
            <KV label={t.customer} value={data.customer} full />
            {data.customer_email && <KV label={t.customerEmail} value={data.customer_email} />}
            {data.work_description && <KV label={t.workDesc} value={data.work_description} full pre />}
          </div>

          {/* Workers table */}
          <div>
            <h3 className="text-sm font-bold text-slate-700 mb-2">{t.workers}</h3>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-100 text-slate-700">
                    <th className="px-2 py-1.5 text-left border border-slate-200">{t.name}</th>
                    <th className="px-2 py-1.5 text-left border border-slate-200">{t.job}</th>
                    <th className="px-2 py-1.5 text-left border border-slate-200">{t.description}</th>
                    <th className="px-2 py-1.5 border border-slate-200">{t.from}</th>
                    <th className="px-2 py-1.5 border border-slate-200">{t.to}</th>
                    <th className="px-2 py-1.5 border border-slate-200">-30min</th>
                    <th className="px-2 py-1.5 border border-slate-200 bg-cyan-50 text-cyan-800">{t.normalHours}</th>
                    <th className="px-2 py-1.5 border border-slate-200 bg-amber-50 text-amber-800">{t.overtimeHours}</th>
                  </tr>
                </thead>
                <tbody>
                  {workerRows.length === 0 && (
                    <tr><td colSpan={8} className="p-2 text-center text-slate-400 italic border border-slate-200">—</td></tr>
                  )}
                  {workerRows.map((w, i) => {
                    const h = computeHours(w, data.date);
                    return (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-2 py-1.5 border border-slate-200">{w.name}</td>
                        <td className="px-2 py-1.5 border border-slate-200">{w.job}</td>
                        <td className="px-2 py-1.5 border border-slate-200">{w.description}</td>
                        <td className="px-2 py-1.5 text-center border border-slate-200 whitespace-nowrap">{w.from_h}:{w.from_m}</td>
                        <td className="px-2 py-1.5 text-center border border-slate-200 whitespace-nowrap">{w.to_h}:{w.to_m}</td>
                        <td className="px-2 py-1.5 text-center border border-slate-200">{w.minus_lunch ? "✓" : "—"}</td>
                        <td className="px-2 py-1.5 text-center border border-slate-200 bg-cyan-50/30 font-semibold text-cyan-800">{fmtH(h.normal)}h</td>
                        <td className={`px-2 py-1.5 text-center border border-slate-200 font-semibold ${h.overtime > 0 ? "bg-amber-50/40 text-amber-800" : "text-slate-400"}`}>{fmtH(h.overtime)}h</td>
                      </tr>
                    );
                  })}
                  {workerRows.length > 0 && (() => {
                    const totals = workerRows.reduce((a, w) => {
                      const h = computeHours(w, data.date);
                      return { normal: a.normal + h.normal, overtime: a.overtime + h.overtime };
                    }, { normal: 0, overtime: 0 });
                    return (
                      <tr className="bg-slate-100 font-bold" data-testid="sign-totals">
                        <td colSpan={6} className="px-2 py-2 text-right border border-slate-200">{t.totalNormal} / {t.totalOvertime}:</td>
                        <td className="px-2 py-2 text-center border border-slate-200 bg-cyan-100 text-cyan-900">{fmtH(totals.normal)}h</td>
                        <td className="px-2 py-2 text-center border border-slate-200 bg-amber-100 text-amber-900">{fmtH(totals.overtime)}h</td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="sm:hidden space-y-2">
              {workerRows.length === 0 && <p className="text-center text-slate-400 italic">—</p>}
              {workerRows.map((w, i) => {
                const h = computeHours(w, data.date);
                return (
                  <div key={i} className="border border-slate-200 rounded-lg p-2 bg-slate-50 text-xs space-y-1">
                    <div className="flex justify-between"><span className="font-bold">{w.name}</span><span className="text-cyan-700">{w.from_h}:{w.from_m} → {w.to_h}:{w.to_m}</span></div>
                    <div className="text-slate-700">{w.job}</div>
                    {w.description && <div className="text-slate-600">{w.description}</div>}
                    <div className="flex gap-2 text-[10px] pt-1">
                      <span className="bg-cyan-100 text-cyan-800 rounded px-1.5 py-0.5 font-semibold">N: {fmtH(h.normal)}h</span>
                      {h.overtime > 0 && <span className="bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 font-semibold">OT: {fmtH(h.overtime)}h</span>}
                      {w.minus_lunch && <span className="text-slate-500">• -30min</span>}
                    </div>
                  </div>
                );
              })}
              {workerRows.length > 0 && (() => {
                const totals = workerRows.reduce((a, w) => {
                  const h = computeHours(w, data.date);
                  return { normal: a.normal + h.normal, overtime: a.overtime + h.overtime };
                }, { normal: 0, overtime: 0 });
                return (
                  <div className="mt-2 grid grid-cols-2 gap-2" data-testid="sign-totals-mobile">
                    <div className="bg-cyan-100 border border-cyan-300 rounded-md p-2 text-center">
                      <div className="text-[10px] font-semibold text-cyan-800 uppercase">{t.totalNormal}</div>
                      <div className="text-lg font-bold text-cyan-800">{fmtH(totals.normal)}h</div>
                    </div>
                    <div className="bg-amber-100 border border-amber-300 rounded-md p-2 text-center">
                      <div className="text-[10px] font-semibold text-amber-800 uppercase">{t.totalOvertime}</div>
                      <div className="text-lg font-bold text-amber-800">{fmtH(totals.overtime)}h</div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {(data.toll_crossings || data.parking) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {data.toll_crossings && <KV label={t.toll} value={data.toll_crossings} />}
              {data.parking && <KV label={t.parking} value={data.parking} />}
            </div>
          )}

          {/* Materials used — only shown if the worker ticked YES */}
          {data.materials_used && (data.materials_net_rolls || data.materials_net_ties || data.materials_plastic_rolls || data.materials_plastic_ties) && (
            <div className="border border-cyan-200 bg-cyan-50 rounded-lg p-3" data-testid="sign-materials">
              <h3 className="text-sm font-bold text-cyan-900 mb-2">{t.materialsTitle}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                {data.materials_net_rolls && <KV label={t.materialsNetRolls} value={data.materials_net_rolls} />}
                {data.materials_net_ties && <KV label={t.materialsNetTies} value={data.materials_net_ties} />}
                {data.materials_plastic_rolls && <KV label={t.materialsPlastRolls} value={data.materials_plastic_rolls} />}
                {data.materials_plastic_ties && <KV label={t.materialsPlastTies} value={data.materials_plastic_ties} />}
              </div>
            </div>
          )}

          {/* Attachments */}
          {(imgAttachments.length > 0 || otherAttachments.length > 0) && (
            <div>
              <h3 className="text-sm font-bold text-slate-700 mb-2">{t.attachments}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {imgAttachments.map((a, i) => (
                  <button
                    type="button"
                    key={i}
                    onClick={() => setLightbox({ url: a.url, name: a.name })}
                    className="block border border-slate-200 rounded overflow-hidden bg-slate-50 group relative hover:border-cyan-500 transition"
                    data-testid={`sign-thumb-${i}`}
                  >
                    <img src={a.url} alt={a.name || `attachment-${i}`} className="w-full h-24 object-cover group-hover:opacity-80 transition" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                      <span className="bg-white/95 text-slate-900 text-[10px] font-semibold px-2 py-0.5 rounded">🔍 Zoom</span>
                    </div>
                  </button>
                ))}
                {otherAttachments.map((a, i) => (
                  <a key={`o-${i}`} href={a.url} target="_blank" rel="noreferrer" className="flex items-center justify-center h-24 border border-slate-200 rounded bg-slate-50 text-xs text-slate-600 p-2 text-center">
                    <FileText className="w-4 h-4 mr-1" /> {a.name || "file"}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Lightbox modal */}
        {lightbox && (
          <div
            className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-2 sm:p-6"
            onClick={() => setLightbox(null)}
            data-testid="lightbox-modal"
          >
            <button
              type="button"
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-slate-900 rounded-full p-2 shadow-lg"
              onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
              data-testid="lightbox-close"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
            <img
              src={lightbox.url}
              alt={lightbox.name || "preview"}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Signature form */}
        <div className="bg-white rounded-xl shadow-lg border-2 border-cyan-500 p-4 sm:p-6 space-y-4">
          <h2 className="text-lg font-bold text-slate-900">{t.customerConf}</h2>

          {/* Prominent toggle — client not present */}
          <button
            type="button"
            onClick={() => setNotPresent(!notPresent)}
            className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition ${
              notPresent
                ? "bg-amber-50 border-amber-500 shadow-sm"
                : "bg-slate-50 border-slate-300 hover:border-slate-400"
            }`}
            data-testid="sign-not-present-btn"
          >
            <div
              className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                notPresent ? "bg-amber-500 border-amber-500" : "bg-white border-slate-400"
              }`}
              style={notPresent ? { color: "#fff" } : {}}
            >
              {notPresent && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
              )}
            </div>
            <div className="flex-1 text-left">
              <div className="text-sm font-bold text-slate-900">{t.notPresent}</div>
              {notPresent && (
                <div className="text-xs text-amber-700 mt-0.5">
                  Underlaget sendes uten signatur til admin + arbeideren. Vi kontakter kunden etterpå.
                </div>
              )}
            </div>
            {/* hidden checkbox for a11y */}
            <input type="checkbox" checked={notPresent} onChange={() => {}} className="sr-only" data-testid="sign-not-present" tabIndex={-1} />
          </button>

          {!notPresent && (
            <>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  {t.yourEmail} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                  data-testid="sign-email"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    {t.firstName} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                    data-testid="sign-firstname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    {t.lastName} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                    data-testid="sign-lastname"
                  />
                </div>
              </div>
            </>
          )}

          {!notPresent && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                {t.signature} <span className="text-red-500">*</span>
              </label>
              <div className="relative border-2 border-dashed border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-lg bg-white touch-none">
                <SignatureCanvas
                  ref={signaturePad}
                  canvasProps={{
                    className: "w-full h-40 rounded-lg cursor-crosshair",
                    "data-testid": "sign-canvas",
                  }}
                  penColor="#0f172a"
                />
                <button
                  type="button"
                  onClick={clearSig}
                  className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                  data-testid="sign-clear"
                >
                  <Trash2 className="w-3 h-3" /> {t.clear}
                </button>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-300 text-white font-bold text-base sm:text-lg rounded-lg shadow-md transition"
            style={{ color: "#fff" }}
            data-testid="sign-submit"
          >
            {submitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> {t.saving}</>) : (notPresent ? "Send uten signatur" : t.submitSignature)}
          </button>
        </div>
      </div>
    </div>
  );
}

function KV({ label, value, full, pre }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-slate-900 ${pre ? "whitespace-pre-wrap" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function FullScreenMessage({ title, text }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-md p-8 max-w-md w-full text-center">
        <div className="text-4xl mb-3">{title}</div>
        <p className="text-slate-700">{text}</p>
      </div>
    </div>
  );
}
